import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getR0TwentyTouch } from '@/lib/r0twentyTouch';

interface AuthProfile {
  full_name: string | null;
  has_paid: boolean;
  created_at?: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: AuthProfile | null;
  purchasedModules: string[];
  loading: boolean;
  isAdmin: boolean;
  adminChecked: boolean;
  signOut: () => Promise<void>;
  refreshPurchases: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


const clearStoredAuth = () => {
  if (typeof window === 'undefined') return;
  const shouldRemove = (key: string) =>
    key === 'session_token' ||
    key === 'supabase.auth.token' ||
    key.startsWith('sb-') ||
    key.includes('-auth-token');

  [window.localStorage, window.sessionStorage].forEach((storage) => {
    try {
      Object.keys(storage)
        .filter(shouldRemove)
        .forEach((key) => storage.removeItem(key));
    } catch { /* ignore */ }
  });
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [purchasedModules, setPurchasedModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const { toast } = useToast();
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchProfile = async (userId: string, email?: string | null, isAnonymous?: boolean) => {
    // Guest (anonymous) sessions have no profile row - skip the lookup entirely
    // instead of firing a request that always 406s.
    if (isAnonymous) {
      setProfile(null);
      setIsAdmin(false);
      setAdminChecked(true);
      return;
    }
    const [{ data }, adminRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('full_name, has_paid, created_at')
        .eq('id', userId)
        .maybeSingle(),
      supabase.rpc('is_admin'),
    ]);

    // Admin status is verified by the server, never by an email in browser code.
    const hasAdminAccess = adminRes.data === true;
    setIsAdmin(hasAdminAccess);
    setAdminChecked(true);

    if (!data) {
      setProfile(hasAdminAccess ? { full_name: null, has_paid: true } : null);
      return;
    }

    setProfile({
      full_name: data.full_name,
      has_paid: Boolean(data.has_paid || hasAdminAccess),
      created_at: (data as any).created_at ?? null,
    });
  };

  const fetchPurchases = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('purchased_modules')
      .select('module')
      .eq('user_id', userId);
    setPurchasedModules((data || []).map(r => r.module));
  }, []);

  const refreshPurchases = useCallback(async () => {
    if (user) {
      await fetchPurchases(user.id);
    }
  }, [user, fetchPurchases]);

  useEffect(() => {
    // Only a definitively malformed token should force a sign-out. Transient
    // network/403 blips must never log a working session out.
    const isBadJwtError = (err: any) => {
      if (!err) return false;
      const msg = (err.message || '').toLowerCase();
      return (
        err.code === 'bad_jwt' ||
        msg.includes('missing sub claim') ||
        msg.includes('invalid claim')
      );
    };

    const forceSignOutBadJwt = async () => {
      try {
        clearStoredAuth();
        await supabase.auth.signOut().catch(() => {});
      } catch { /* ignore */ }
      clearStoredAuth();
      setSession(null);
      setUser(null);
      setProfile(null);
      setPurchasedModules([]);
      setLoading(false);
    };

    let disposed = false;
    const authTasks = new Set<ReturnType<typeof setTimeout>>();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Auth awaits its subscribers while holding its session lock. Run
        // auth-dependent work in a later task so getUser/signOut cannot deadlock
        // recovery verification or updateUser.
        const task = setTimeout(() => {
          authTasks.delete(task);
          if (disposed) return;
          void handleAuthChange().catch(() => { if (!disposed) setLoading(false); });
        }, 0);
        authTasks.add(task);
        async function handleAuthChange() {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Validate JWT is actually accepted by the auth server. Stale/rotated
          // tokens can arrive here with a malformed `sub` claim; if so, clear
          // them instead of letting downstream RLS-guarded inserts fail.
          if (event === 'SIGNED_IN') {
            const { error: userErr } = await supabase.auth.getUser();
            if (userErr && isBadJwtError(userErr)) {
              await forceSignOutBadJwt();
              return;
            }
          }

          // Register the single-session token on real sign-ins only. Doing this on
          // TOKEN_REFRESHED rotated the token and kicked the user off their own device.
          if (event === 'SIGNED_IN' && !(session.user as any)?.is_anonymous && !localStorage.getItem('session_token')) {
            const sessionToken = crypto.randomUUID();
            localStorage.setItem('session_token', sessionToken);
            // Fire-and-forget so a transient RLS/network failure never blocks sign-in.
            supabase.from('active_sessions').upsert(
              { user_id: session.user.id, session_token: sessionToken, created_at: new Date().toISOString() },
              { onConflict: 'user_id' }
            ).then(() => {});

            // For OAuth signups, notify admin (check if profile was just created).
            // Guest (anonymous) sessions have no profile row, so skip them.
            if (event === 'SIGNED_IN' && !(session.user as any)?.is_anonymous) {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('created_at')
                .eq('id', session.user.id)
                .maybeSingle();

              if (profileData) {
                const createdAt = new Date(profileData.created_at).getTime();
                const now = Date.now();
                // If profile was created in the last 30 seconds, it's a new signup
                if (now - createdAt < 30000) {
                  // Save referral source + full attribution payload for OAuth signups
                  const referralSource = localStorage.getItem('referral_source') || sessionStorage.getItem('referral_source');
                  const attributionRaw = localStorage.getItem('attribution') || sessionStorage.getItem('attribution');
                  const updates: Record<string, any> = {};
                  if (referralSource) updates.referral_source = referralSource;
                  let attributionObj: Record<string, any> = {};
                  if (attributionRaw) {
                    try { attributionObj = JSON.parse(attributionRaw); } catch { /* ignore */ }
                  }
                  const r0Touch = getR0TwentyTouch();
                  if (r0Touch) attributionObj = { ...attributionObj, ...r0Touch };
                  if (Object.keys(attributionObj).length > 0) updates.attribution = attributionObj;
                  if (Object.keys(updates).length > 0) {
                    supabase.from('profiles').update(updates as any).eq('id', session.user.id).then(() => {});
                  }


                  // Send branded welcome email to the user
                  supabase.functions.invoke('send-welcome-email').catch(() => {});
                }
              }
            }
          }
          setTimeout(() => {
            fetchProfile(session.user.id, session.user.email, (session.user as any)?.is_anonymous);
            fetchPurchases(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setPurchasedModules([]);
        }
        setLoading(false);
        }
      }
    );


    // Safety net: if the auth network calls below never settle (common when iOS
    // resumes a backgrounded home-screen app and its sockets are dead), stop
    // showing the loading screen instead of hanging until the app is force-closed.
    const loadingGuard = setTimeout(() => setLoading(false), 6000);

    // Never let a hung request block startup.
    const withTimeout = <T,>(p: Promise<T>, ms = 5000): Promise<T | null> =>
      Promise.race([p, new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))]);

    withTimeout(supabase.auth.getSession()).then(async (res) => {
      const session = res?.data?.session ?? null;
      const error = res?.error;
      // Detect a corrupt/stale JWT (e.g. missing `sub` claim) and clear it
      // so the user isn't stuck with a token the auth server rejects.
      const isBadJwt = (err: any) => {
        if (!err) return false;
        const msg = (err.message || '').toLowerCase();
        return (
          err.code === 'bad_jwt' ||
          msg.includes('missing sub claim') ||
          msg.includes('invalid claim')
        );
      };

      if (error && isBadJwt(error)) {
        try {
          // Wipe any stored auth keys + our own session token, then sign out cleanly
          clearStoredAuth();
          await supabase.auth.signOut().catch(() => {});
        } catch { /* ignore */ }
        setSession(null);
        setUser(null);
        setLoading(false);
        clearTimeout(loadingGuard);
        return;
      }

      // If we got a session but /user calls fail with bad_jwt, also clear it
      if (session?.user) {
        const userRes = await withTimeout(supabase.auth.getUser(), 4000);
        const userErr = userRes?.error;
        if (userErr && isBadJwt(userErr)) {
          try {
            clearStoredAuth();
            await supabase.auth.signOut().catch(() => {});
          } catch { /* ignore */ }
          setSession(null);
          setUser(null);
          setLoading(false);
          clearTimeout(loadingGuard);
          return;
        }
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id, session.user.email, (session.user as any)?.is_anonymous);
        fetchPurchases(session.user.id);
      }
      setLoading(false);
      clearTimeout(loadingGuard);
    });

    // When the app is brought back to the foreground (installed app resumed),
    // re-read the session so a dead/expired token is refreshed straight away
    // rather than leaving the screen stuck.
    let lastVisibleCheck = 0;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Debounce: don't re-check on every rapid tab switch.
      const now = Date.now();
      if (now - lastVisibleCheck < 10000) return;
      lastVisibleCheck = now;

      withTimeout(supabase.auth.getSession(), 4000).then((res) => {
        // A timeout (null) or an errored read tells us nothing about whether the
        // user is signed in - never clear a live session on that basis. Sign-outs
        // and token refreshes arrive via onAuthStateChange instead.
        const s = res?.data?.session ?? null;
        if (!s) {
          setLoading(false);
          return;
        }
        setSession(s);
        setUser(s.user ?? null);
        setLoading(false);
      });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      authTasks.forEach(clearTimeout);
      clearTimeout(loadingGuard);
      document.removeEventListener('visibilitychange', onVisible);
      subscription.unsubscribe();
    };
  }, []);


  // Watch for session being replaced by another login
  useEffect(() => {
    if (!user) {
      // Clean up any existing channel
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      return;
    }

    const localToken = localStorage.getItem('session_token');
    if (!localToken) return;

    const channel = supabase
      .channel(`session-guard-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'active_sessions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newToken = (payload.new as { session_token: string }).session_token;
          if (newToken !== localToken) {
            toast({
              title: 'Signed out',
              description: 'Another device signed in with your account. Only one session is allowed at a time.',
              variant: 'destructive',
            });
            supabase.auth.signOut();
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      realtimeChannelRef.current = null;
    };
  }, [user, toast]);

  // Handle post-social-OAuth redirect stored by Login/Register pages
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem('social_oauth_redirect');
    if (pending) {
      sessionStorage.removeItem('social_oauth_redirect');
      // Only allow same-origin relative paths - reject absolute/protocol-relative URLs.
      const safe = pending.startsWith('/') && !pending.startsWith('//') && !pending.startsWith('/\\')
        ? pending
        : '/dashboard';
      const current = window.location.pathname + window.location.search;
      if (current !== safe) {
        window.location.href = safe;
      }
    }
  }, [user]);

  const signOut = async () => {
    // Fire-and-forget the active_sessions cleanup so a hanging/failed request
    // never blocks the actual sign-out.
    const userId = user?.id ?? session?.user?.id;
    if (userId) {
      supabase.from('active_sessions').delete().eq('user_id', userId).then(() => {});
    }
    clearStoredAuth();
    try {
      await supabase.auth.signOut();
    } catch { /* ignore */ }
    clearStoredAuth();
    setSession(null);
    setUser(null);
    setProfile(null);
    setPurchasedModules([]);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, purchasedModules, loading, isAdmin, adminChecked, signOut, refreshPurchases }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
